import {
	TimeSeriesRawDataLatest,
	TimeSeriesRawDataReadings,
	TimeSeriesRawDataTotal,
} from '../../interfaces/api/timeseries';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { TimeSeriesRawDataParam, TimeSeriesValueType } from '../../api/timeseries';
import {
	fetchRawDataLatest,
	fetchRawDataReadings,
	fetchRawDataTotal,
} from '../services/timeseries.service';

interface TimeSeriesState {
	rawDataLatest: {
		[tag: number]: TimeSeriesRawDataLatest[];
	};
	rawDataReadings: {
		[tag: number]: { [type in TimeSeriesValueType]: TimeSeriesRawDataReadings[] };
	};
	rawDataTotal: {
		[tag: number]: TimeSeriesRawDataTotal[];
	};
	isLoading: boolean;
}

const initialState = {
	rawDataLatest: [],
	rawDataReadings: [],
	rawDataTotal: [],
	isLoading: false,
} as TimeSeriesState;

export const fetchRawDataLatestThunk = createAsyncThunk<
	{ rawDataLatest: TimeSeriesRawDataLatest[]; tags: string[] },
	{ param: TimeSeriesRawDataParam }
>('timeseries/fetchRawDataLatest', async ({ param }, { rejectWithValue }) => {
	try {
		const rawDataLatest = await fetchRawDataLatest(param);
		return {
			rawDataLatest: rawDataLatest,
			tags: param.tags,
		};
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const fetchRawDataReadingsThunk = createAsyncThunk<
	{ rawDataReadings: TimeSeriesRawDataReadings[]; tags: string[]; valueType: TimeSeriesValueType },
	{ param: TimeSeriesRawDataParam }
>('timeseries/fetchRawDataReadings', async ({ param }, { rejectWithValue }) => {
	try {
		const rawDataReadings = await fetchRawDataReadings(param);
		return {
			rawDataReadings: rawDataReadings,
			tags: param.tags,
			valueType: param.type ? param.type : 'avg',
		};
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const fetchRawDataTotalThunk = createAsyncThunk<
	{ rawDataTotal: TimeSeriesRawDataTotal[]; tags: string[] },
	{ param: TimeSeriesRawDataParam }
>('timeseries/fetchRawDataTotal', async ({ param }, { rejectWithValue }) => {
	try {
		const rawDataTotal = await fetchRawDataTotal(param);
		return {
			rawDataTotal: rawDataTotal,
			tags: param.tags,
		};
	} catch (error) {
		return rejectWithValue(error);
	}
});

const timeseriesSlice = createSlice({
	name: 'timeseries',
	initialState,
	reducers: {
		resetTimeseries: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchRawDataLatestThunk.fulfilled, (state, action) => {
			for (const tag of action.payload.tags) {
				delete state.rawDataLatest[Number(tag)];
				const tagRawDataLatest: TimeSeriesRawDataLatest[] = action.payload.rawDataLatest.filter(
					(rawDataLatest) => rawDataLatest.tag === Number(tag)
				);
				state.rawDataLatest = {
					...state.rawDataLatest,
					[Number(tag)]: [...(state.rawDataLatest[Number(tag)] ?? []), ...tagRawDataLatest],
				};
			}
			state.isLoading = false;
		});
		builder.addCase(fetchRawDataLatestThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchRawDataLatestThunk.rejected, (state, action) => {
			state.rawDataLatest = [];
			state.isLoading = false;
		});

		builder.addCase(fetchRawDataReadingsThunk.fulfilled, (state, action) => {
			for (const tag of action.payload.tags) {
				if (state.rawDataReadings[Number(tag)]?.[action.payload.valueType]) {
					delete state.rawDataReadings[Number(tag)][action.payload.valueType];
				}
				const tagRawDataReadings: TimeSeriesRawDataReadings[] =
					action.payload.rawDataReadings.filter(
						(rawDataReadings) => rawDataReadings.tag === Number(tag)
					);
				state.rawDataReadings = {
					...state.rawDataReadings,
					[Number(tag)]: {
						...(state.rawDataReadings[Number(tag)] ?? []),
						[action.payload.valueType]: [
							...(state.rawDataReadings[Number(tag)]?.[action.payload.valueType] ?? []),
							...tagRawDataReadings,
						],
					},
				};
			}
			state.isLoading = false;
		});
		builder.addCase(fetchRawDataReadingsThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchRawDataReadingsThunk.rejected, (state, action) => {
			state.rawDataReadings = [];
			state.isLoading = false;
		});

		builder.addCase(fetchRawDataTotalThunk.fulfilled, (state, action) => {
			for (const tag of action.payload.tags) {
				delete state.rawDataTotal[Number(tag)];
				const tagRawDataTotal: TimeSeriesRawDataTotal[] = action.payload.rawDataTotal.filter(
					(rawDataTotal) => rawDataTotal.tag === Number(tag)
				);
				state.rawDataTotal = {
					...state.rawDataTotal,
					[Number(tag)]: [...(state.rawDataTotal[Number(tag)] ?? []), ...tagRawDataTotal],
				};
			}
			state.isLoading = false;
		});
		builder.addCase(fetchRawDataTotalThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchRawDataTotalThunk.rejected, (state, action) => {
			state.rawDataTotal = [];
			state.isLoading = false;
		});
	},
});

export const { resetTimeseries } = timeseriesSlice.actions;

export default timeseriesSlice;
